import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { CinematicStage, Pill, DemoBadge } from "@/components/shared";
import { HERO_IMAGE, ROOM_BACKDROPS } from "@/lib/studio";
import FilmCard from "@/components/FilmCard";

const QUICK_IDEAS = [
  "A space adventure",
  "A historical drama",
  "A nature documentary",
  "A fantasy world",
];

const DEMO_FILMS = [
  { id: "demo-1", title: "The Signal Between", cover: ROOM_BACKDROPS.floor, status: "ready", config: { style: "Sci-Fi", duration: "12 min" } },
  { id: "demo-2", title: "Waves of Kolkata", cover: ROOM_BACKDROPS.theater, status: "generating", config: { style: "Documentary", duration: "10 min" } },
  { id: "demo-3", title: "Dusk at Riva", cover: ROOM_BACKDROPS.writers, status: "queued", config: { style: "Drama", duration: "8 min" } },
  { id: "demo-4", title: "Aurora Protocol", cover: ROOM_BACKDROPS.stage, status: "ready", config: { style: "Adventure", duration: "15 min" } },
];

const FLOATERS = [ROOM_BACKDROPS.stage, ROOM_BACKDROPS.theater, ROOM_BACKDROPS.control];

export default function Home() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const launch = () => navigate("/create", { state: { prompt } });
  const hasReal = projects.length > 0;

  return (
    <CinematicStage>
      {/* ---------- HERO: Creator Command Center ---------- */}
      <motion.section
        initial={{ opacity: 0, scale: 1.01 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative rounded-[1.75rem] overflow-hidden border border-white/10 letterbox"
        data-testid="home-hero"
      >
        {/* Environment image */}
        <div className="absolute inset-0 z-0">
          <motion.div
            initial={{ scale: 1.08 }} animate={{ scale: 1 }}
            transition={{ duration: 8, ease: "easeOut" }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
        </div>
        {/* Scrims */}
        <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(90deg, rgba(7,7,13,0.97) 0%, rgba(7,7,13,0.78) 42%, rgba(7,7,13,0.28) 75%, rgba(7,7,13,0.55) 100%)" }} />
        <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(0deg, rgba(7,7,13,0.9) 0%, transparent 55%)" }} />

        {/* Floating film frames (alive) */}
        {FLOATERS.map((img, i) => (
          <motion.div
            key={i}
            className="hidden xl:block absolute z-[2] rounded-xl overflow-hidden border border-white/15 shadow-2xl"
            style={{
              width: 150, height: 92,
              top: 40 + i * 118, right: 70 + (i % 2) * 120,
              backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center",
            }}
            animate={{ y: [0, i % 2 ? 12 : -12, 0] }}
            transition={{ duration: 7 + i, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-black/25" />
          </motion.div>
        ))}

        {/* Content */}
        <div className="relative z-10 px-6 sm:px-10 lg:px-14 py-12 sm:py-16 lg:py-20 flex flex-col lg:flex-row gap-8">
          <div className="max-w-2xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-slate-400 mb-5">
              A New Era of Storytelling
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              What will you <span className="text-gradient">create</span> today?
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-5 max-w-xl">
              Turn your imagination into cinematic reality with AI. From idea to screen — all in <span className="text-white font-semibold">one studio</span>.
            </p>

            {/* Command console */}
            <div className="mt-8 rounded-2xl p-2.5 flex flex-col sm:flex-row gap-2.5 border border-white/12 bg-black/40 backdrop-blur-xl shadow-[0_20px_60px_-24px_rgba(0,0,0,0.9)]">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Sparkles className="w-4 h-4 text-[#7C70FF] shrink-0" />
                <input
                  data-testid="home-prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && launch()}
                  placeholder="Describe your idea… (e.g. A space adventure on a lost planet)"
                  className="flex-1 bg-transparent outline-none py-2.5 text-white placeholder:text-slate-500 text-sm sm:text-base"
                />
              </div>
              <button
                onClick={launch}
                data-testid="home-launch-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#5B4DFF] to-[#7C70FF] hover:from-[#4a3ff0] hover:to-[#6a5eff] text-white font-semibold transition-all whitespace-nowrap shadow-[0_0_22px_rgba(91,77,255,0.5)]"
              >
                Start Creating <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {QUICK_IDEAS.map((q) => (
                <button
                  key={q}
                  onClick={() => setPrompt(q)}
                  data-testid="home-idea-chip"
                  className="text-xs sm:text-sm px-3.5 py-1.5 rounded-full bg-white/[0.06] border border-white/12 text-slate-300 hover:border-[#5B4DFF]/50 hover:text-white hover:bg-white/10 transition"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Right editorial quote */}
          <div className="hidden lg:flex flex-col justify-start ml-auto text-right pt-2 max-w-[220px]">
            <p className="font-display italic text-xl leading-snug text-slate-200">
              "Every Great Story Finds a New World."
            </p>
            <div className="mt-4 h-px w-16 bg-white/20 ml-auto" />
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500 mt-4 leading-relaxed">
              Same imagination.<br />Bigger possibilities.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ---------- RECENT PRODUCTIONS ---------- */}
      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="mt-12"
      >
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-display text-2xl font-semibold flex items-center gap-2.5">
              Recent Productions {!hasReal && <DemoBadge />}
            </h2>
            <p className="text-slate-500 text-sm mt-1">Continue your creative journey</p>
          </div>
          <button onClick={() => navigate("/projects")} data-testid="home-view-all" className="text-sm text-[#b6aaff] hover:text-white flex items-center gap-1.5">
            View All <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(hasReal ? projects.slice(0, 4) : DEMO_FILMS).map((p, i) => (
            <FilmCard key={p.id} project={p} demo={!hasReal} index={i} />
          ))}
        </div>
        {!hasReal && (
          <p className="text-xs text-slate-500 mt-3">
            Labelled demo films so the studio feels alive — start creating above to produce your first real film.
          </p>
        )}
      </motion.section>
    </CinematicStage>
  );
}

export function StatusPill({ status }) {
  const map = {
    ready: { tone: "green", label: "Completed" },
    generating: { tone: "gold", label: "In Progress" },
    queued: { tone: "purple", label: "Draft" },
    failed: { tone: "red", label: "Failed" },
  };
  const s = map[status] || { tone: "slate", label: status };
  return <Pill tone={s.tone}>{s.label}</Pill>;
}
